import { useState, useEffect } from "react";
import {
  X,
  Download,
  Share2,
  Copy,
  Check,
  Film,
  Clock,
  Calendar,
  ExternalLink,
  Search,
  Loader,
  AlertCircle,
  RefreshCw,
  Play,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import {
  fetchZoomClips,
  fetchZoomClipDownloadUrl,
  ingestZoomClip,
} from "../../services/api";

export default function ZoomClipsDrawer({ open, onClose }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [ingestingId, setIngestingId] = useState(null);
  const [ingestedIds, setIngestedIds] = useState(new Set());
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadClips = async (opts = {}) => {
    try {
      const isLoadMore = !!opts.pageToken;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      const result = await fetchZoomClips({
        search: opts.search ?? search,
        pageToken: opts.pageToken,
        pageSize: 50,
      });

      if (isLoadMore) {
        setClips((prev) => [...prev, ...(result.data || [])]);
      } else {
        setClips(result.data || []);
      }
      setTotalRecords(result.total_records || 0);
      setNextPageToken(result.next_page_token || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (open) loadClips();
  }, [open]);

  if (!open) return null;

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    loadClips({ search: searchInput });
  };

  const handleCopyShareLink = (clip) => {
    if (!clip.share_link) return;
    navigator.clipboard.writeText(clip.share_link);
    setCopiedId(clip.clip_id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownload = async (clip) => {
    setDownloadingId(clip.clip_id);
    try {
      const result = await fetchZoomClipDownloadUrl(clip.clip_id);
      if (result.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
      }
    } catch (err) {
      console.error("Download failed:", err);
      // Fallback: try the share link
      if (clip.share_link) {
        window.open(clip.share_link, "_blank");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpenShareLink = (clip) => {
    if (clip.share_link) {
      window.open(clip.share_link, "_blank");
    }
  };

  const handleIngest = async (clip) => {
    setIngestingId(clip.clip_id);
    try {
      const result = await ingestZoomClip(clip.clip_id, clip.clip_name || clip.title, clip.duration);
      setIngestedIds((prev) => new Set(prev).add(clip.clip_id));
      if (result.triggerWarning) {
        alert(`Clip saved but pipeline trigger failed: ${result.triggerWarning}`);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIngestingId(null);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0)
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 99,
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 560,
          background: colors.bgCard,
          borderLeft: `1px solid ${colors.border}`,
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-20px 0 60px rgba(0,0,0,0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 28px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Film size={20} color={colors.info} />
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 18,
                  color: colors.text,
                  margin: 0,
                }}
              >
                Zoom Clips
              </h2>
              {totalRecords > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 10,
                    background: "rgba(99,102,241,0.1)",
                    color: colors.info,
                    fontWeight: 600,
                  }}
                >
                  {totalRecords}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={() => loadClips()}
                title="Refresh"
                style={{
                  background: "none",
                  border: "none",
                  color: colors.textSecondary,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.textSecondary,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} style={{ display: "flex", gap: 8 }}>
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                background: colors.bgSurface,
              }}
            >
              <Search size={14} color={colors.textSecondary} />
              <input
                type="text"
                placeholder="Search clips..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  background: "none",
                  flex: 1,
                  fontSize: 13,
                  fontFamily: fonts.body,
                  color: colors.text,
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                padding: "8px 16px",
                background: colors.info,
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontSize: 13,
                fontFamily: fonts.body,
                cursor: "pointer",
              }}
            >
              Search
            </button>
          </form>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 28px",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 40,
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              <Loader
                size={16}
                className="spin"
              />
              Loading Zoom Clips...
            </div>
          ) : error ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: colors.error,
                fontSize: 13,
              }}
            >
              <AlertCircle
                size={24}
                style={{ marginBottom: 8, opacity: 0.6 }}
              />
              <div style={{ marginBottom: 8 }}>{error}</div>
              <button
                onClick={() => loadClips()}
                style={{
                  padding: "6px 16px",
                  background: "rgba(239,68,68,0.08)",
                  border: `1px solid ${colors.error}`,
                  borderRadius: 6,
                  color: colors.error,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                Retry
              </button>
            </div>
          ) : clips.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 40,
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              <Film
                size={28}
                style={{ marginBottom: 10, opacity: 0.4 }}
                color={colors.textSecondary}
              />
              <div style={{ marginBottom: 4 }}>No Zoom Clips found</div>
              <div style={{ fontSize: 11 }}>
                Clips created in Zoom will appear here.
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {clips.map((clip) => (
                <ClipCard
                  key={clip.clip_id}
                  clip={clip}
                  copiedId={copiedId}
                  downloadingId={downloadingId}
                  ingestingId={ingestingId}
                  ingestedIds={ingestedIds}
                  onCopyShareLink={handleCopyShareLink}
                  onDownload={handleDownload}
                  onOpen={handleOpenShareLink}
                  onIngest={handleIngest}
                  formatDuration={formatDuration}
                  formatDate={formatDate}
                  formatFileSize={formatFileSize}
                />
              ))}

              {/* Load more */}
              {nextPageToken && (
                <div style={{ textAlign: "center", paddingTop: 8 }}>
                  <button
                    onClick={() => loadClips({ pageToken: nextPageToken })}
                    disabled={loadingMore}
                    style={{
                      padding: "8px 24px",
                      background: "rgba(99,102,241,0.08)",
                      border: `1px solid ${colors.border}`,
                      borderRadius: 6,
                      color: colors.info,
                      fontSize: 13,
                      fontFamily: fonts.body,
                      cursor: loadingMore ? "default" : "pointer",
                      opacity: loadingMore ? 0.6 : 1,
                    }}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>    </>
  );
}

function ClipCard({
  clip,
  copiedId,
  downloadingId,
  ingestingId,
  ingestedIds,
  onCopyShareLink,
  onDownload,
  onOpen,
  onIngest,
  formatDuration,
  formatDate,
  formatFileSize,
}) {
  const isCopied = copiedId === clip.clip_id;
  const isDownloading = downloadingId === clip.clip_id;
  const isIngesting = ingestingId === clip.clip_id;
  const isIngested = ingestedIds.has(clip.clip_id);

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        overflow: "hidden",
        background: colors.bgCard,
      }}
    >
      {/* Thumbnail / preview area */}
      {clip.thumbnail_link ? (
        <div
          style={{
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => onOpen(clip)}
        >
          <img
            src={clip.thumbnail_link}
            alt={clip.title || "Zoom Clip"}
            style={{
              width: "100%",
              height: 180,
              objectFit: "cover",
              display: "block",
              background: "#111",
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          {/* Duration badge */}
          {clip.duration > 0 && (
            <span
              style={{
                position: "absolute",
                bottom: 8,
                right: 8,
                padding: "2px 6px",
                background: "rgba(0,0,0,0.75)",
                color: "#fff",
                fontSize: 11,
                borderRadius: 4,
                fontFamily: fonts.mono,
              }}
            >
              {formatDuration(clip.duration)}
            </span>
          )}
        </div>
      ) : (
        <div
          style={{
            height: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.06)",
            cursor: "pointer",
          }}
          onClick={() => onOpen(clip)}
        >
          <Film size={24} color={colors.textSecondary} />
          {clip.duration > 0 && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 12,
                color: colors.textSecondary,
                fontFamily: fonts.mono,
              }}
            >
              {formatDuration(clip.duration)}
            </span>
          )}
        </div>
      )}

      {/* Clip info */}
      <div style={{ padding: "12px 14px" }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: colors.text,
            marginBottom: 6,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {clip.title || "Untitled Clip"}
        </div>

        {clip.description && (
          <div
            style={{
              fontSize: 12,
              color: colors.textSecondary,
              lineHeight: 1.4,
              marginBottom: 8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {clip.description}
          </div>
        )}

        {/* Meta row */}
        <div
          style={{
            display: "flex",
            gap: 12,
            fontSize: 11,
            color: colors.textSecondary,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          {clip.created_date && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Calendar size={10} />
              {formatDate(clip.created_date)}
            </span>
          )}
          {clip.duration > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Clock size={10} />
              {formatDuration(clip.duration)}
            </span>
          )}
          {clip.file_size > 0 && <span>{formatFileSize(clip.file_size)}</span>}
          {clip.share_link_settings?.share_scope && (
            <span
              style={{
                padding: "1px 5px",
                borderRadius: 3,
                background: "rgba(99,102,241,0.08)",
                color: colors.info,
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              {clip.share_link_settings.share_scope === "ANYONE"
                ? "Public"
                : clip.share_link_settings.share_scope === "SAME_ORGANIZATON"
                  ? "Org Only"
                  : clip.share_link_settings.share_scope ===
                      "INVITED_MEMBERS_ONLY"
                    ? "Invited"
                    : "Private"}
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <ActionButton
            icon={isIngested ? Check : Play}
            label={
              isIngested
                ? "Queued"
                : isIngesting
                  ? "Processing..."
                  : "Process"
            }
            onClick={() => onIngest(clip)}
            disabled={isIngesting || isIngested}
            color={isIngested ? colors.success : colors.info}
          />
          {clip.share_link && (
            <ActionButton
              icon={ExternalLink}
              label="Open in Zoom"
              onClick={() => onOpen(clip)}
            />
          )}
          <ActionButton
            icon={Download}
            label={isDownloading ? "Downloading..." : "Download"}
            onClick={() => onDownload(clip)}
            disabled={isDownloading}
          />
          {clip.share_link && (
            <ActionButton
              icon={isCopied ? Check : Copy}
              label={isCopied ? "Copied!" : "Copy Link"}
              onClick={() => onCopyShareLink(clip)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, color, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "5px 10px",
        background: "none",
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        color: color || colors.textSecondary,
        fontSize: 11,
        fontFamily: fonts.body,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}
